import { rem } from 'polished'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import TextButton from 'components/Luxkit/Button/TextButton'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import useNavigateTo from 'hooks/useNavigateTo'
import { addQueryParamsToPath } from 'lib/url/searchUrlUtils'
import { useIsMobileScreen } from 'hooks/useScreenSize'
import BookedOfferDetailsButtonAndTagView from 'tripPlanner/components/ItemCard/Common/BookedOfferDetailsButtonAndTagView'
import FlightItemCardContent from 'tripPlanner/components/ItemCard/Flight/FlightItemCardContent'
import FlightSearchTileContent from 'tripPlanner/components/SavedItemTiles/FlightSearchTileContent'
import TripItemSummary from 'tripPlanner/components/TripModal/TripItems/Summary'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { FlightItem } from 'tripPlanner/types/tripItem'
import { isLeBooking, isLeFlightItem } from 'tripPlanner/utils'
import { buildStandaloneFlightSearchURL } from 'tripPlanner/utils/bookmark/restoreCart'
import { tripItemSelectionId } from 'tripPlanner/utils/itemSelection'
import AboveCardCloseButton from '../Common/AboveCardCloseButton'

const CardContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-around;
`

const Container = styled.div`
  position: relative;
  width: 100%;
  background-color: white;
  padding: ${rem(16)};
  pointer-events: auto;
  box-shadow: ${props => props.theme.shadow.bottom.medium};

  ${mediaQueryUp.largeDesktop} {
    max-width: ${rem(700)};
    padding: ${rem(24)};
  }
`

const StyledTextButton = styled(TextButton)`
  padding-top: ${rem(8)};
  place-self: end;
`

const MainGroup = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`

interface Props {
  className?: string
  item: FlightItem
  showCloseButton?: boolean
  onClose?: () => void
}

const FlightItemSummaryCard = React.forwardRef<HTMLDivElement, Props>(
  ({ className, item, showCloseButton, onClose }, ref) => {
    const openModal = useOpenModal()
    const openDetailsModal = useMemo(() => {
      return () => openModal(TripItemSummary, { item })
    }, [item, openModal])

    const isMobileScreen = useIsMobileScreen()
    const source = useTripClientSource()
    const flightSearchURL = item.savedItemData ?
      addQueryParamsToPath(
        buildStandaloneFlightSearchURL(item.savedItemData),
        {
          source,
        },
      ) :
      undefined
    const navigateTo = useNavigateTo()
    const { setImmersiveMode } = useImmersiveModeHandlers()

    const searchForFlight = useCallback(() => {
      if (flightSearchURL) {
        navigateTo(flightSearchURL)
        setImmersiveMode({
          sectionId: tripItemSelectionId(item.id),
          tripId: item.tripId,
        })
      }
    }, [
      flightSearchURL,
      item.id,
      item.tripId,
      navigateTo,
      setImmersiveMode,
    ])

    const showDetail = useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        openModal(TripItemSummary, { item })
        e.stopPropagation()
      },
      [item, openModal],
    )

    const isFlightSearch =
      item.type === 'FLIGHT' &&
      item.sourceType === 'LE_Flight' &&
      (!item.startDate || !item.startTime || !item.endDate || !item.endTime)

    return (
      <CardContainer className={className}>
        <Container ref={ref}>
          {showCloseButton && (
            <AboveCardCloseButton onClick={onClose} />
          )}
          <MainGroup onClick={isMobileScreen ? openDetailsModal : undefined}>
            {!isFlightSearch && (
              <Group direction="vertical" gap={8}>
                <FlightItemCardContent item={item} variant="mapSummary" />
                <BookedOfferDetailsButtonAndTagView item={item} size="small" />
              </Group>
            )}
            {!isFlightSearch && !isLeBooking(item) && (
              <StyledTextButton
                kind="tertiary"
                size="small"
                variant="default"
                outdent="right"
                nonInteractable
                onClick={showDetail}
              >
                View details
              </StyledTextButton>
            )}
            {isFlightSearch && isLeFlightItem(item) && (
              <FlightSearchTileContent
                item={item}
                variant="mapSummary"
                onFlightSearchClick={searchForFlight}
              />
            )}
          </MainGroup>
        </Container>
      </CardContainer>
    )
  },
)

FlightItemSummaryCard.displayName = 'FlightItemSummaryCard'
export default FlightItemSummaryCard
